import { AxiosPromise } from 'axios';

import { ChallengeResultOrderType, ChallengeTypeType } from 'interfaces';
import { CHALLENGE } from 'constants/index';
import { api } from './api';

const MAX_SUPPORTED_ITEMS_NUMBER = 1000;
const DEFAULT_ITEMS_NUMBER = 9;

export class ChallengesAPI {
  static getUserChallenges(): AxiosPromise {
    return api.get(`/user/challenges`);
  }

  static getUserChallenge(challengeId: number): AxiosPromise {
    return api.get(`/challenges/${challengeId}/user.jsonld`);
  }

  static getUserChallengeDeclarations(challengeId: number): AxiosPromise {
    return api.get(`/declarations/challenge/${challengeId}/user`);
  }

  static getUserChallengeDeclaration(challengeId: number): AxiosPromise {
    return api.get(`/challenge-lists/${challengeId}/user.jsonld`);
  }

  static postChallengeDeclaration(data: any): AxiosPromise {
    return api.post('declarations', data, {
      headers: { 'content-type': 'multipart/form-data' },
    });
  }

  static getUserGlobalView(type: string | null, status: string): AxiosPromise {
    return api.get('/challenges/user.jsonld', {
      params: type ? { type, status } : { status },
    });
  }

  static getTotalChallengeParticipants(id: number): AxiosPromise {
    return api.get(`/challenges/${id}/total-participants`);
  }

  static getTotalUsersParticipation(id: number): AxiosPromise {
    return api.get(`/challenges/${id}/challenge-results/participating`);
  }

  static postUserParticipation(
    challengeId: number,
    value: Record<string, unknown>,
  ): AxiosPromise {
    return api.post(
      `/challenges/${challengeId}/participation/user.jsonld`,
      value,
    );
  }

  static getChallengeResults({
    challengeId,
    page,
    order,
    challengeType,
    status = 'validated',
    isFetchingAll,
  }: {
    challengeId: number;
    page?: number;
    order?: ChallengeResultOrderType;
    challengeType?: ChallengeTypeType;
    status?: string;
    isFetchingAll?: boolean;
  }): AxiosPromise {
    return api.get(`/challenges/${challengeId}/challenge-results.jsonld`, {
      params: {
        page,
        itemsPerPage: isFetchingAll
          ? MAX_SUPPORTED_ITEMS_NUMBER
          : DEFAULT_ITEMS_NUMBER,
        'order[rank]': order,
        ...(challengeType === CHALLENGE.PHOTO ? { status } : {}),
      },
    });
  }

  static getChallengeListResults(challengeId: number) {
    return api.get(`/challenge/${challengeId}/results`);
  }

  static getChallengeResultsModeration(challengeId: number): AxiosPromise {
    return api.get(`/challenges/${challengeId}/moderation`);
  }

  static getChallengeRanks(challengeId: number): AxiosPromise {
    return api.get(`/challenges/${challengeId}/rank/user`);
  }

  static postUserChallengePhotoResults(
    challengeId: number,
    photoId: number,
  ): AxiosPromise {
    return api.post('/challenge-results/user/photo', {
      challenge: `challenges/${challengeId}`,
      image: `/media-objects/${photoId}`,
    });
  }

  static getChallengeResultVote(resultId: number): AxiosPromise {
    return api.get(`/challenge-results/${resultId}/user/vote.jsonld`);
  }

  static getChallengeResultUnvote(resultId: number): AxiosPromise {
    return api.get(`/challenge-results/${resultId}/user/unvote.jsonld`);
  }

  static deleteChallengeResult(resultId: number): AxiosPromise {
    return api.delete(`/challenge-results/${resultId}`);
  }
}
